
import React from 'react';
import { OrderStatus } from '../types';
import { CheckCircle2, Clock, Package, Truck, Home, CreditCard, Sparkles } from 'lucide-react';

interface OrderTimelineProps {
  status: OrderStatus;
  updatedAt?: string;
}

const OrderTimeline: React.FC<OrderTimelineProps> = ({ status, updatedAt }) => {
  const steps: { key: OrderStatus; label: string; icon: any; desc: string }[] = [
    { key: 'PENDING', label: 'Order Placed', icon: Clock, desc: 'Wait for verification' },
    { key: 'PAID', label: 'Payment Confirmed', icon: CreditCard, desc: 'Securely received' },
    { key: 'PROCESSING', label: 'Processing', icon: Package, desc: 'Preparing for dispatch' },
    { key: 'SHIPPED', label: 'Shipped', icon: Truck, desc: 'In transit to city' },
    { key: 'DELIVERED', label: 'Delivered', icon: Home, desc: 'Received successfully' },
  ];

  const currentIdx = steps.findIndex(s => s.key === status);
  const isCancelled = status === 'CANCELLED';

  return (
    <div className="space-y-10 py-6">
      {isCancelled ? (
        <div className="bg-red-50 border-2 border-red-100 p-8 rounded-[2.5rem] flex items-center gap-6 animate-in zoom-in">
           <div className="w-16 h-16 bg-red-600 text-white rounded-2xl flex items-center justify-center shadow-lg shadow-red-200">
              <CheckCircle2 className="h-8 w-8 rotate-45" />
           </div>
           <div>
              <h3 className="text-xl font-black text-red-900">Order Cancelled</h3>
              <p className="text-sm text-red-600 font-medium">This transaction was terminated. If payment was made, refund is initiated.</p>
           </div>
        </div>
      ) : (
        <div className="relative">
          {/* Vertical Line */}
          <div className="absolute left-[27px] top-6 bottom-6 w-0.5 bg-gray-100"></div>

          <div className="space-y-10 relative">
            {steps.map((step, idx) => {
              const isPast = idx < currentIdx;
              const isCurrent = idx === currentIdx;
              const isFuture = idx > currentIdx;
              
              const Icon = step.icon;

              return (
                <div key={step.key} className={`flex gap-6 transition-all duration-700 ${isFuture ? 'opacity-30' : 'opacity-100'}`}>
                  <div className={`relative z-10 w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-500 border-4 ${
                    isPast ? 'bg-blue-600 text-white border-blue-100 scale-90' : 
                    isCurrent ? 'bg-white text-blue-600 border-blue-600 shadow-xl shadow-blue-100 scale-110' : 
                    'bg-white text-gray-300 border-gray-100'
                  }`}>
                    <Icon className={`h-6 w-6 ${isCurrent ? 'animate-pulse' : ''}`} />
                    {isCurrent && (
                       <div className="absolute -top-1 -right-1 bg-blue-600 text-white p-1 rounded-full shadow-lg">
                          <Sparkles className="h-2 w-2 animate-spin-slow" />
                       </div>
                    )}
                  </div>
                  <div className="flex-grow pt-1">
                    <h4 className={`font-black text-lg ${isCurrent ? 'text-blue-600' : 'text-gray-900'}`}>{step.label}</h4>
                    <p className="text-xs text-gray-400 font-bold uppercase tracking-widest mt-0.5">{step.desc}</p>
                    {isCurrent && updatedAt && (
                      <p className="text-[10px] text-blue-500 font-black mt-2 uppercase tracking-tighter">Updated: {new Date(updatedAt).toLocaleString()}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderTimeline;
